import type { DocumentId, ModelId, RevisionId } from '@/shared/types/common.types';
import type { RevisionStatus } from '@/features/documents/types/document.types';
import type { PersonRef } from './workspace.types';

/** Models of a project grouped by the discipline set on the link; `null` when none was set. */
export interface DisciplineGroup {
  readonly discipline: string | null;
  readonly count: number;
  /** The latest synchronisation in the group; `null` when none of its models has been synchronised. */
  readonly lastSynchronized: string | null;
}

export interface RecentDocument {
  readonly id: DocumentId;
  readonly title: string;
  readonly documentNumber: string | null;
  /** The status of the revision the caller sees as current. */
  readonly status: RevisionStatus;
  readonly updatedAt: string;
  readonly updatedBy: PersonRef;
}

/**
 * `GET /api/v1/projects/{id}/summary`: what the project dashboard shows, in one request.
 * Counts are of what the caller may see.
 */
export interface ProjectSummary {
  readonly models: { readonly total: number; readonly byDiscipline: readonly DisciplineGroup[] };
  readonly documents: {
    readonly total: number;
    readonly byStatus: readonly { readonly status: RevisionStatus; readonly count: number }[];
    readonly recent: readonly RecentDocument[];
  };
  readonly sheets: { readonly total: number };
  readonly members: { readonly total: number; readonly pending: number };
  readonly activeManifest: {
    readonly documentId: DocumentId;
    readonly documentNumber: string | null;
    readonly revisionId: RevisionId;
    readonly revisionCode: string;
  } | null;
}

export const HEALTH_STATUSES = ['Ready', 'Attention', 'NoData', 'Unknown'] as const;
/** `NoData` is a model that has never been synchronised: nothing can be judged, so it is never shown as passing. */
export type HealthStatus = (typeof HEALTH_STATUSES)[number];

export const CHECK_STATES = ['Passed', 'Failed', 'Unavailable'] as const;
/** `Unknown` when a newer Core sends a state this client does not know yet; it is never shown as passing. */
export type CheckState = (typeof CHECK_STATES)[number] | 'Unknown';

export interface HealthCheck {
  readonly key: string;
  readonly label: string;
  readonly state: CheckState;
  readonly detail: string;
}

export interface ModelHealth {
  readonly modelId: ModelId;
  readonly title: string;
  readonly discipline: string | null;
  readonly status: HealthStatus;
  readonly modelVersion: number | null;
  readonly lastSynchronized: string | null;
  readonly checks: readonly HealthCheck[];
}

/** `GET /api/v1/projects/{id}/health`: every linked model's checks in one request. */
export interface ProjectHealth {
  readonly generatedAt: string;
  /** True when Core could not read the model data; the roll-up is then empty and must not look healthy. */
  readonly degraded: boolean;
  readonly overall: {
    readonly status: HealthStatus;
    readonly models: number;
    readonly ready: number;
    readonly attention: number;
    readonly noData: number;
  };
  readonly models: readonly ModelHealth[];
}
